import { Injectable } from '@angular/core';
import { Icon, Style, Fill, Stroke, Circle } from 'ol/style';
import { LineString, Point, Polygon } from 'ol/geom';
import { Feature } from 'ol';
import { Extent, boundingExtent } from 'ol/extent';
import { Subject, Observable } from 'rxjs';
import Layer from 'ol/layer/Layer';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import BingMaps from 'ol/source/BingMaps';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import Map from 'ol/Map';
import View from 'ol/View';
import * as olProj from 'ol/proj';
import { Draw } from 'ol/interaction';
import ImageLayer from 'ol/layer/Image';
import VectorTileLayer from 'ol/layer/VectorTile';
import Cluster from 'ol/source/Cluster';
import { Text } from 'ol/style';
import Overlay from 'ol/Overlay';

export interface PointData {
  id: string,
  fLon: number,
  fLat: number,
  icon: string,
  anchor?: number[],
  scale?: number,
  size?: number[],
  rotation?: number,
  rotateWithView?: boolean
}
export interface FitOptions {
  padding?: number | number[];
  duration?: number;
  maxZoom?: number;
}
export interface LocationClickData {
  longitude: number;
  latitude: number;
  feature?: Feature | unknown;
}
export interface GeolocationData {
  longitude: number;
  latitude: number;
  accuracy: number;
  timestamp: number;
}
export interface StyleOptions {
  strokeColor?: string;
  strokeWidth?: number;
  fillColor?: string;
}
export interface PolygonData {
  coordinates: [number, number][];
  style?: PolygonStyleOptions;
  id?: string | number;
}
export interface PolygonStyleOptions {
  strokeColor?: string;
  strokeWidth?: number;
  fillColor?: string;
}
export interface TooltipOptions {
  textColor?: string,
  textFillColor?: string,
  textFont?: string,
  textStrokeColor?: string,
  textStrokeWidth?: number,
  offsetX?: number,
  offsetY?: number,
  textAlign?: string,
  textBaseline?: string
}

const DEFAULT_STROKE_COLOR = 'rgba(255, 0, 0, 0.5)';
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_FILL_COLOR = 'rgba(255, 0, 0, 0.1)';

@Injectable({
  providedIn: 'root'
})

export class MapBuilderService {
  private vectorSource: VectorSource;

  private vectorLayer: VectorLayer<VectorSource>;
  private baseLayers: TileLayer<XYZ | OSM | BingMaps>[] = [];
  private extraLayers = new window.Map<string | number, Layer>();
  private _autoLayerCounter = 0;

  private mapInstance: Map | null = null;
  private location = [51.3833554, 35.6770113]; // تهران

  private strMapUrl = (<any>window).mapUrl;
  private useOfflineMap = this.strMapUrl ? true : false;
  private maxZoom = this.useOfflineMap ? ((<any>window).mapMaxZoom ?? 14) : 24;

  private clusterSources = new window.Map<string, Cluster>();
  private clusterLayers = new window.Map<string, VectorLayer<Cluster>>();

  private tooltips = new window.Map<string, Overlay>();

  private markerElement: HTMLElement | null = null;

  private mapClickSubject = new Subject<LocationClickData>();
  mapClick$: Observable<LocationClickData> = this.mapClickSubject.asObservable();

  private markerHoverSubject = new Subject<any>();
  markerHover$: Observable<any> = this.markerHoverSubject.asObservable();

  private lastKnownLocationSubject = new Subject<GeolocationData>();
  lastKnownLocation$: Observable<GeolocationData> = this.lastKnownLocationSubject.asObservable();

  private isDrawingModeActive = false;
  private drawInteraction: Draw | null = null;

  private polygonSubject = new Subject<PolygonData>();
  polygonDraw$: Observable<PolygonData> = this.polygonSubject.asObservable();

  constructor() {
    // this.vectorSource = new VectorSource({ wrapX: false });
    this.vectorSource = this.createVectorSource({ wrapX: false });

    // this.vectorLayer = new VectorLayer({ source: this.vectorSource });
    const vectorLayer = this.createLayer('Vector', { source: this.vectorSource }, 'baseLayer');
    this.vectorLayer = (vectorLayer as VectorLayer<VectorSource>);


    this.setupBaseLayers();
  }

  private setupBaseLayers(): void {
    if (this.useOfflineMap) {
      const offlinelayerId = 'rayanAndisheh';
      const offlineLayer = new TileLayer({
        source: new XYZ({
          url: this.strMapUrl,
          attributions: 'Map data © RayanAndishehNasr'
        }),
      })

      offlineLayer.set('layerId', offlinelayerId);
      this.baseLayers.push(offlineLayer);
    }

    else {
      const styles = ['AerialWithLabelsOnDemand'];
      for (let i = 0, ii = styles.length; i < ii; ++i) {
        const bingLayerId = `bing`;
        const bingLayer = new TileLayer({
          visible: false,
          preload: Infinity,
          source: new BingMaps({
            key: 'Ap0AObt84NcDaUThCeWOj52ZqUHv6k4TJhjLibR-DghC-semgoj-0uPbIi8r0E4j',
            imagerySet: styles[i],
          }),
        });

        bingLayer.set('layerId', bingLayerId);
        this.baseLayers.push(bingLayer);
      }

      const osmLayerId = 'osm';
      const osmLayer = new TileLayer({ source: new OSM({}) });

      osmLayer.set('layerId', osmLayerId);
      this.baseLayers.push(osmLayer);

    }
  }

  /**
   * @param additionalLayers - Additional layers to add to the map.
   * @returns 
   */

  public getMapLayers(additionalLayers: any[] = []): any[] {
    const standardLayers = [...this.baseLayers, this.vectorLayer, ...additionalLayers];
    const extraFromStore = Array.from(this.extraLayers.values());
    return [...standardLayers, ...extraFromStore];
  }

  public getVectorSource(): VectorSource {
    return this.vectorSource;
  }

  public getVectorLayer(): VectorLayer<VectorSource> {
    return this.vectorLayer;
  }

  /**
   * Creates and returns a complete map instance.
   * @param targetId - The DOM element ID where the map will be rendered.
   * @param location - The center of the map in [longitude, latitude].
   * @param zoom - The initial zoom level of the map.
   * @param additionalLayers - Additional layers to add to the map.
   * @returns The Map instance.
   */

  public createMap(targetId: string, location: number[] = this.location, zoom: number = 14, additionalLayers: any[] = []): Map {

    if (this.mapInstance) {
      this.mapInstance.setTarget(null);
      // Remove draw interaction if map is being re-created
      if (this.drawInteraction) {
        this.mapInstance.removeInteraction(this.drawInteraction);
        this.drawInteraction = null;
        this.isDrawingModeActive = false; // Reset drawing mode flag
      }
    }

    const allLayers = this.getMapLayers(additionalLayers);

    this.mapInstance = new Map({
      target: targetId,
      view: new View({
        zoom: Math.min(zoom, this.maxZoom),
        maxZoom: this.maxZoom,
        center: olProj.fromLonLat(location)
      }),
      layers: allLayers,
    });

    this.registerMapEvents();

    return this.mapInstance;

  }

  private registerMapEvents() {

    if (!this.isDrawingModeActive) {

      this.mapInstance.on('pointermove', (event) => {
        if (event.dragging) return;
        const feature = this.mapInstance?.forEachFeatureAtPixel(event.pixel, (feature) => feature);
        const tooltipExists = this.getMarkerDataFromFeature(feature)?.tooltip;

        if (tooltipExists) {
          this.mapInstance.getViewport().style.cursor = 'pointer';
          this.markerHoverSubject.next(feature);
        }

        else {
          this.markerHoverSubject.next(undefined);
          this.mapInstance.getViewport().style.cursor = '';
        }

      });

      this.mapInstance.on('click', (event) => {
        if (event.dragging) return;

        const lonLat = olProj.toLonLat(event.coordinate);
        const clickedFeature = this.mapInstance?.forEachFeatureAtPixel(event.pixel, (feature) => feature);

        const clickData: LocationClickData = {
          longitude: lonLat[0],
          latitude: lonLat[1],
          feature: clickedFeature
        };

        this.mapClickSubject.next(clickData);

        this.mapInstance.getView().animate({
          center: olProj.transform([lonLat[0], lonLat[1]], 'EPSG:4326', 'EPSG:3857'),
          duration: 500
        });


      });

      this.mapInstance.getView().on('change:center', () => {
        // const center = this.mapInstance.getView().getCenter();
        // const lonLat = olProj.toLonLat(center as [number, number]);
        // this.mapClickSubject.next({ longitude: lonLat[0], latitude: lonLat[1] });

        const olMarker = this.markerElement;
        if (olMarker) {
          olMarker.classList.add('movestart')
        }
      });

      this.mapInstance.on('moveend', () => {
        const olMarker = this.markerElement;
        if (olMarker) {
          olMarker.classList.remove('movestart')
        }
      })

    }

    else if (this.isDrawingModeActive) {
      this.deactivatePolygonDrawing();
      this.isDrawingModeActive = false;
    }

  }

  /**
 * Extracts marker data from a feature if it exists.
 * This method is intended to be used after a map click event.
 * @param feature - The clicked OpenLayers Feature.
 * @returns The marker data associated with the feature, or undefined if not found.
 */
  public getMarkerDataFromFeature(feature: any): any | undefined {
    if (feature && typeof feature.get === 'function') {
      const markerData = feature.get('marker_data');
      if (markerData) {
        return markerData;
      }
    }
    return undefined;
  }

  /**
   * @param feature - The OpenLayers Feature to add (ol/Feature).
   * @param vectorSource - Optional: The target VectorSource to add the feature to. If not provided,
   *                       this.vectorSource will be used.
   */

  public addFeatureToVectorSource(feature: any, vectorSource?: VectorSource): void {
    const targetSource = vectorSource ?? this.vectorSource;
    if (!targetSource) {
      console.warn('No vector source available to add the feature.');
      return;
    }
    targetSource.addFeature(feature);
  }

  /**
   * 
   * @param vectorSource 
   * @returns 
   */

  public clearVectorSource(vectorSource?: VectorSource): void {
    const targetSource = vectorSource ?? this.vectorSource;
    if (!targetSource) {
      console.warn('No vector source available to add the feature.');
      return;
    }
    targetSource.clear();
  }

  /**
 * Creates and returns a new VectorSource.
 * @param options - Optional options for the VectorSource (e.g., wrapX).
 * @returns A new VectorSource instance.
 */
  public createVectorSource(options?: any): VectorSource {
    const sourceOptions = options ?? { wrapX: false };
    return new VectorSource(sourceOptions);
    // { source: myCustomSource }
  }

  /**
   * @param type - The type of layer to create.
   * @param options - A configuration object containing initialization options for the specific
   * @param id - Optional identifier for the created layer. If not provided, an automatic
   *   identifier is generated in the format 'auto-<n>'
   * @returns The newly created Layer instance corresponding to the requested type.
   */

  public createLayer(type: 'Tile' | 'Vector' | 'Image' | 'XYZ' | 'VectorTile', options: any, id?: string | number): Layer {
    let layer: Layer;

    switch (type) {
      case 'Tile':
        layer = new TileLayer(options);
        break;
      case 'Vector':
        layer = new VectorLayer(options);
        break;
      case 'Image':
        layer = new ImageLayer(options);
        break;
      case 'XYZ':
        layer = new TileLayer({ source: new XYZ(options) });
        break;
      case 'VectorTile':
        layer = new VectorTileLayer(options);
        break;
      default:
        throw new Error(`Unsupported layer type: ${type}`);
    }

    const finalId = id ?? `auto-${++this._autoLayerCounter}`;
    layer.set('layerId', finalId);

    return layer;
  }

  /**
 * Adds a layer to the map at runtime. Optionally sets an id on the layer.
 * @param layer - any ol.layer.Layer instance (TileLayer, VectorLayer, etc.)
 * @param id - optional id (string | number) to later identify/remove the layer
 */

  public addLayer(layer: Layer, id?: string | number): void {

    const finalId = id ?? `auto-${++this._autoLayerCounter}`;
    this.extraLayers.set(finalId, layer);
    layer.set('layerId', finalId);

    if (!this.mapInstance) {
      console.warn('Map instance is not created yet. Layer will be stored and added when map is created.');
      return;
    }

    this.mapInstance.addLayer(layer);

  }

  /**
 * Helper: create a TileLayer from URL (XYZ) and add it directly
 * @param url - tile URL template (e.g. 'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png')
 * @param id - optional id
 * @param opts - optional XYZ options like attributions
 */

  public addTileLayerFromUrl(url: string, id?: string | number, opts?: any): TileLayer<any> {
    const tile = new TileLayer({ source: new XYZ({ url, ...opts }) });
    const finalId = id ?? `auto-${++this._autoLayerCounter}`;
    (tile as any).set('layerId', finalId);
    this.extraLayers.set(finalId, tile);
    this.addLayer(tile, finalId);
    return tile;
  }

  /**
   * 
   * @param id 
   * @returns 
   */

  public getLayerById(id: string | number): Layer | undefined {
    if (this.extraLayers.has(id)) return this.extraLayers.get(id);

    if (this.mapInstance) {
      const layersArray = this.mapInstance.getLayers().getArray();
      for (const l of layersArray) {
        if ((l as any).get && (l as any).get('layerId') === id) {
          return l as Layer;
        }
      }
    }

    return undefined;
  }

  /**
 * Removes a layer from the map by reference or by id.
 * @param layerOrId - the layer instance or the id assigned with addLayer
 */

  /**
 * Reorder map layers according to the given order.
 * Higher index in the array => drawn above earlier ones.
 * Each id should correspond to a layer that has a 'layerId' set (either in extraLayers or on the layer itself).
 *
 * @param orderedIds - Array of layer ids in the desired drawing order (bottom to top).
 */
  public setLayerOrder(orderedIds: (string | number)[]): void {
    if (!this.mapInstance) {
      console.warn('Map instance is not available yet.');
      return;
    }

    // Build a set: id -> layer
    const idToLayer = new window.Map<string | number, any>();

    for (const [id, layer] of this.extraLayers.entries()) {
      idToLayer.set(id, layer);
    }

    const layersOnMap = this.mapInstance.getLayers().getArray();
    for (const l of layersOnMap) {
      const lid = (l as any).get && (l as any).get('layerId');
      if (lid != null) {
        if (!idToLayer.has(lid)) idToLayer.set(lid, l);
      }
    }

    // Apply order via zIndex: bottom = 0 .. top = N-1
    orderedIds.forEach((id, index) => {
      const layer = idToLayer.get(id);
      if (layer) {
        (layer as any).setZIndex(index);
      } else {
        console.warn(`Layer with id "${id}" not found for ordering.`);
      }
    });

    // Optional: render to ensure visuals update immediately
    // this.mapInstance.render();

  }

  /**
 * Toggles the visibility of a layer by its ID.
 * @param layerId - The ID of the layer to toggle.
 * @param visible - Whether to make the layer visible (true) or hidden (false).
 */
  public setLayerVisibility(layerId: string | number, visible: boolean): void {
    const layer = this.getLayerById(layerId);
    if (layer) {
      layer.setVisible(visible);
      console.log(`Layer "${layerId}" visibility set to: ${visible}`);
    } else {
      console.warn(`Layer with ID "${layerId}" not found.`);
    }
  }

  /**
 * Sets the opacity of a layer.
 * @param layerId - The ID of the layer to adjust.
 * @param opacity - The desired opacity value (0 to 1).
 */
  public setLayerOpacity(layerId: string | number, opacity: number): void {
    const layer = this.getLayerById(layerId);
    if (layer) {
      const validOpacity = Math.max(0, Math.min(1, opacity));
      layer.setOpacity(validOpacity);
      console.log(`Layer "${layerId}" opacity set to: ${validOpacity}`);
    } else {
      console.warn(`Layer with ID "${layerId}" not found.`);
    }
  }

  /**
   * 
   * @param layerOrId 
   * @returns 
   */

  public removeLayer(layerOrId: Layer | string | number): void {
    if (!this.mapInstance) {
      console.warn('Map instance is not available.');
      if (typeof layerOrId !== 'object') {
        this.extraLayers.delete(layerOrId);
        console.warn('But layer found and deleted from storage.');
      }
      return;
    }

    if (typeof layerOrId === 'object') {
      this.mapInstance.removeLayer(layerOrId as Layer);
      for (const [k, v] of this.extraLayers.entries()) {
        if (v === layerOrId) {
          this.extraLayers.delete(k);
          break;
        }
      }
      return;
    }

    // find layer by stored property 'layerId'
    const id = layerOrId;
    const layers = this.mapInstance.getLayers().getArray();
    const found = layers.find(l => (l as any).get && (l as any).get('layerId') === id);
    if (found) {
      this.mapInstance.removeLayer(found);
      this.extraLayers.delete(id);
    } else {
      console.warn(`Layer with id "${id}" not found.`);
    }
  }

  /**
   * Toggles marker clustering for a specific vector layer.
   * When enabled, markers from the specified layer that are close to each other will be grouped.
   * @param enableClustering - Set to true to enable clustering, false to disable.
   * @param layerId - The ID of the vector layer to apply clustering to.
   * @param distance - The distance in pixels between clusters. Default is 40.
   * @param clusterStyleFn - Optional function to create custom styles for cluster markers.
   * @param animate - Whether to use animation for showing/hiding layers. Default is true.
   */

  public toggleMarkerClustering(enableClustering: boolean, layerId?: string | number, distance: number = 40, clusterStyleFn?: (clusterFeature: Feature) => Style): void {
    if (!this.mapInstance) {
      console.warn('Map instance is not available. Clustering cannot be toggled.');
      return;
    }

    let targetVectorSource: VectorSource | undefined;
    let targetVectorLayer: VectorLayer<any> | undefined;

    if (layerId !== undefined && layerId !== null) {
      const layer = this.getLayerById(layerId);
      if (layer && layer instanceof VectorLayer) {
        targetVectorSource = layer.getSource() as VectorSource;
        targetVectorLayer = layer as VectorLayer<any>;
      } else {
        console.warn(`Layer with id "${layerId}" not found or is not a VectorLayer.`);
        return;
      }
    }

    else {
      targetVectorSource = this.vectorSource;
      targetVectorLayer = this.vectorLayer;
      if (!targetVectorSource) {
        console.error('Default vector source not found.');
        return;
      }
    }

    const clusterLayerId = `cluster_${layerId ?? 'default'}`;
    const clusterSourceId = `clusterSource_${layerId ?? 'default'}`;

    if (enableClustering) {
      console.log(`Enabling marker clustering for layer "${layerId ?? 'default'}".`);

      const defaultClusterStyle = (clusterFeature: Feature): Style => {
        const count = (clusterFeature.get('features') || []).length;
        const text = count.toString();
        return new Style({
          image: new Circle({ radius: 15, fill: new Fill({ color: 'rgba(255, 0, 0, 0.6)' }), stroke: new Stroke({ color: 'white', width: 2 }) }),
          text: new Text({ text: text, fill: new Fill({ color: 'white' }), font: '12px sans-serif', textAlign: 'center', textBaseline: 'middle', offsetX: 0, offsetY: 0 })
        });
      };

      const styleFunction = clusterStyleFn ?? defaultClusterStyle;

      if (this.clusterLayers.has(clusterLayerId)) {
        const existingClusterLayer = this.clusterLayers.get(clusterLayerId);
        if (existingClusterLayer) {
          existingClusterLayer.setVisible(true);
          console.log(`Clustering re-enabled for layer "${layerId ?? 'default'}".`);
          if (targetVectorLayer) targetVectorLayer.setVisible(false);
          return;
        }
      }

      const newClusterSource = new Cluster({
        distance: distance,
        source: targetVectorSource,
        geometryFunction: (feature) => {
          const geometry = feature.getGeometry();
          if (geometry && geometry.getType() === 'Point') return geometry as Point;
          return null;
        }
      });
      this.clusterSources.set(clusterSourceId, newClusterSource);

      const newClusterLayer = new VectorLayer<Cluster>({
        source: newClusterSource,
        style: styleFunction
      });

      newClusterLayer.set('layerId', clusterLayerId);
      this.extraLayers.set(clusterLayerId, newClusterLayer);
      this.mapInstance.addLayer(newClusterLayer);
      this.clusterLayers.set(clusterLayerId, newClusterLayer);

      if (targetVectorLayer) targetVectorLayer.setVisible(false);

      console.log('Marker clustering enabled.');

    }

    else {
      console.log('Disabling marker clustering...');

      const clusterLayerToRemove = this.clusterLayers.get(`cluster_${layerId ?? 'default'}`);

      if (clusterLayerToRemove) {
        clusterLayerToRemove.setVisible(false);
        console.log(`Clustering disabled for layer "${layerId ?? 'default'}".`);

        if (targetVectorLayer) {
          targetVectorLayer.setVisible(true);
        }
      } else {
        console.log(`Clustering for layer "${layerId ?? 'default'}" was already disabled or not initialized.`);
      }
    }
  }

  /**
   * Creates a function that returns a custom cluster style.
   * @param options - Options like radius, fillColor, textColor.
   * @returns A function that takes a cluster feature and returns an OpenLayers Style object.
   */
  public getCustomClusterStyleFunction(options?: { radius?: number, fillColor?: string, textColor?: string }): (clusterFeature: Feature) => Style {
    const radius = options?.radius ?? 15;
    const fillColor = options?.fillColor ?? 'rgba(0, 0, 255, 0.6)';
    const textColor = options?.textColor ?? 'white';

    return (clusterFeature: Feature): Style => {
      const count = (clusterFeature.get('features') || []).length;
      const text = count.toString();

      return new Style({
        image: new Circle({
          radius: radius,
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: 'white', width: 2 })
        }),
        text: new Text({
          text: text,
          fill: new Fill({ color: textColor }),
          font: 'bold 12px sans-serif',
          textAlign: 'center',
          textBaseline: 'middle',
          offsetX: 0,
          offsetY: 0
        })
      });
    };
  }

  /**
 * Adds multiple points (markers) to the map.
 * @param businessPoints - An array of data objects, each with fLon and fLat.
 * @param validIconSrc - The icon source for valid points.
 * @param invalidIconSrc - The icon source for invalid points.
 */

  public addMarkers(businessPoints: any[], validIconSrc: string, invalidIconSrc?: string): void {

    businessPoints.forEach((point: any) => {

      let iconSrc: string;

      if (point?.bValid === true) {
        iconSrc = validIconSrc;
      }

      else {
        iconSrc = invalidIconSrc ?? validIconSrc;
      }

      const iconStyle = new Style({
        image: new Icon({
          src: iconSrc,
          size: [40, 38],
          anchor: [0.5, 1]
        }),
      });

      const marker = new Feature({
        geometry: new Point(olProj.fromLonLat([point.fLon, point.fLat])),
      });

      marker.setStyle(iconStyle);
      marker.set('marker_data', point);

      this.addFeatureToVectorSource(marker);
    });
  }

  /**
 * Adds or updates markers on the map. If a marker with the same ID exists, it's updated.
 * @param pointsData - An array of data objects, each with unique 'id', 'fLon', 'fLat', and ...'.
 * @param iconSrc - The icon source for points.
 * @param layerId - Optional add maker to specefic layer
 */

  public addOrUpdateMarkers(pointsData: PointData[], iconSrc: string, layerId?: string | number): void {

    let vectorSource = this.vectorSource;

    if (layerId !== undefined && layerId !== null) {
      const layer = this.getLayerById(layerId) as any;
      if (layer && layer.getSource) {
        vectorSource = layer.getSource() as VectorSource;
      }

      else {
        console.warn(`Layer with id "${layerId}" not found or not a vector layer. use default source.`);
      }
    }

    if (!vectorSource) {
      console.error('Vector source not found.');
      return;
    }

    const existingFeatures = vectorSource.getFeatures();
    const featureMap = new window.Map<string, Feature>();

    existingFeatures.forEach((feature: any) => {
      const id = feature.getId?.() ?? feature.get('id');
      if (id) {
        featureMap.set(String(id), feature);
      }
    });

    pointsData.forEach((point: PointData) => {
      const pointId = point.id;
      const iconStyle = new Style({
        image: new Icon({
          src: iconSrc,
          anchor: point.anchor ?? [0.5, 0.5],
          scale: point.scale ?? 1,
          rotation: point.rotation ?? 0,
          rotateWithView: point.rotateWithView ?? false,
          // size: point.size ?? [40, 38],
        }),
      });

      if (featureMap.has(pointId)) {
        // Update existing feature
        const feature = featureMap.get(pointId)!;
        feature.setGeometry(new Point(olProj.fromLonLat([point.fLon, point.fLat])));
        feature.setStyle(iconStyle);
        feature.set('marker_data', point); // Update data
        featureMap.delete(pointId); // Remove from map to track removals
      }

      else {
        // Add new feature
        const newFeature = new Feature({
          geometry: new Point(olProj.fromLonLat([point.fLon, point.fLat])),
        });
        newFeature.setId(pointId);
        newFeature.setStyle(iconStyle);
        newFeature.set('marker_data', point);
        this.addFeatureToVectorSource(newFeature, vectorSource);
      }

    });

  }

  /**
   * Fits the map view to the extent of a given set of coordinates (route).
   * @param coordinates - An array of [longitude, latitude] pairs defining the route.
   * @param options - Optional configuration for fitting the map.
   *   - padding: Padding around the extent in pixels [top, right, bottom, left].
   *   - duration: Animation duration in milliseconds.
   *   - maxZoom: Maximum zoom level to zoom to.
   */

  public fitMapToCoordinates(coordinates: [number, number][], options?: FitOptions): void {

    if (!this.mapInstance) {
      console.warn('Map instance is not available yet.');
      return;
    }

    if (!coordinates || coordinates.length === 0) {
      console.warn('No coordinates provided to fitMapToRoute.');
      return;
    }

    const transformedCoords = coordinates.map(coord => olProj.fromLonLat(coord));

    const extent: Extent = boundingExtent(transformedCoords);

    const defaultOptions = {
      padding: [80, 80, 80, 80], // Default padding if not provided
      duration: 1000,           // Default duration
      maxZoom: this.maxZoom    // Default max zoom
    };

    const fitOptions: unknown = { ...defaultOptions, ...options };

    this.mapInstance.getView().fit(extent, fitOptions);

  }

  /**
 * Removes a feature (marker) from the vector source by its ID.
 * @param featureId - The ID of the feature to remove.
 */

  public removeFeatureById(featureId: string | number): void {
    const feature = this.vectorSource.getFeatureById(featureId);
    if (feature) {
      this.vectorSource.removeFeature(feature);
    } else {
      console.warn(`Feature with ID "${featureId}" not found.`);
    }
  }

  /**
    * Gets the current location of the user as an Observable.
    * Emits the location data once and then completes.
    * @returns An Observable that emits the user's current location.
    */

  public requestCurrentLocation$(): Observable<GeolocationData> {
    console.log('Requesting user location...');
    return new Observable<GeolocationData>(observer => {
      if (!navigator.geolocation) {
        observer.error('Geolocation is not supported by this browser.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData: GeolocationData = {
            longitude: position.coords.longitude,
            latitude: position.coords.latitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          };
          observer.next(locationData);
          observer.complete();
        },
        (error: any) => {
          let errorMessage = 'خطایی رخ داده که دلیل آن مشخص نیست.';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "لطفاً برای استفاده از این قابلیت، اجازه دسترسی به موقعیت مکانی خود را صادر کنید.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "امکان تعیین موقعیت مکانی فعلی شما وجود ندارد. لطفاً اتصال اینترنت یا GPS خود را بررسی کنید.";
              break;
            case error.TIMEOUT:
              errorMessage = "درخواست برای دریافت موقعیت مکانی شما بیش از حد طول کشید. لطفاً دوباره تلاش کنید.";
              break;
            case error.UNKNOWN_ERROR:
              errorMessage = "خطایی غیرمنتظره در هنگام دریافت موقعیت مکانی رخ داده است.";
              break;
          }
          observer.error(`${errorMessage}`);

        }, {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000
      }
      );
    });
  }


  /**
   * Adds a Polygon to the map with flexible coordinate input.
   * @param coordinates - array of [longitude, latitude] pairs for the exterior ring,
   * @param styleOptions - Optional styling for the polygon (strokeColor, strokeWidth, fillColor).
   * @param id - Optional ID for the polygon feature.
   */

  public addPolygon(coordinates: [number, number][], styleOptions?: StyleOptions, id?: string | number): void {
    let polygonCoords: number[][][];
    polygonCoords = [coordinates as [number, number][]];

    const projectedPolygonCoords = polygonCoords.map(ring =>
      ring.map(coord => olProj.fromLonLat(coord))
    );

    const polygonGeometry = new Polygon(projectedPolygonCoords);

    const defaultStyleOptions: Required<StyleOptions> = {
      strokeColor: '#000000',
      strokeWidth: 1,
      fillColor: 'rgba(0, 0, 0, 0.1)',
    };

    const mergedStyleOptions = { ...defaultStyleOptions, ...styleOptions };

    const polygonStyle = new Style({
      stroke: new Stroke({
        color: mergedStyleOptions.strokeColor,
        width: mergedStyleOptions.strokeWidth,
      }),
      fill: new Fill({
        color: mergedStyleOptions.fillColor,
      }),
    });

    const polygonFeature = new Feature({
      geometry: polygonGeometry,
    });

    if (id !== undefined && id !== null) {
      polygonFeature.setId(id);
    }

    polygonFeature.setStyle(polygonStyle);

    this.addFeatureToVectorSource(polygonFeature);

  }

  /**
   * Toggles the drawing mode for polygons.
   * If activated, clicking on the map will add points to a polygon. Double-click to finish.
   * If deactivated, map clicks return to normal behavior.
   * @param styleOptions - Optional styling for the polygon (stroke color and width).
   *   - strokeColor:
   *   - strokeWidth:
   *   - fillColor:
   */

  public togglePolygonDrawing(styleOptions?: PolygonStyleOptions): void {
    this.isDrawingModeActive = !this.isDrawingModeActive;

    const style = this.createPolygonStyle(styleOptions);

    if (this.isDrawingModeActive) {
      console.log('Polygon drawing mode activated.');
      this.activatePolygonDrawing(style, styleOptions);
    } else {
      console.log('Polygon drawing mode deactivated.');
      this.deactivatePolygonDrawing();
    }
  }

  /**
   * 
   * @param styleOptions 
   * @returns 
   */

  private createPolygonStyle(styleOptions?: PolygonStyleOptions): Style {
    return new Style({
      stroke: new Stroke({
        color: styleOptions?.strokeColor ?? DEFAULT_STROKE_COLOR,
        lineDash: [10, 10],
        width: styleOptions?.strokeWidth ?? DEFAULT_STROKE_WIDTH,
      }),
      fill: new Fill({
        color: styleOptions?.fillColor ?? DEFAULT_FILL_COLOR,
      }),
    });
  }

  /**
   * 
   * @param polygonStyle 
   * @param styleOptions 
   */

  private activatePolygonDrawing(polygonStyle: Style, styleOptions?: PolygonStyleOptions): void {

    this.deactivatePolygonDrawing(); // Remove existing draw interaction

    document.body.classList.add('mapDrawingMode');

    this.drawInteraction = new Draw({
      source: this.vectorSource,
      type: 'Polygon',
      style: polygonStyle,
    });

    this.mapInstance?.addInteraction(this.drawInteraction);

    // Listen for drawend event
    this.drawInteraction.on('drawend', (event: any) => {
      const feature = event.feature as Feature;
      const geometry = feature.getGeometry() as Polygon;
      const coordinates = geometry.getCoordinates()[0].map(coord => olProj.toLonLat(coord));

      const id = new Date().getTime();
      const polygonData: PolygonData = {
        id: id,
        coordinates: coordinates as any,
        style: {
          strokeColor: styleOptions?.strokeColor ?? DEFAULT_STROKE_COLOR,
          strokeWidth: styleOptions?.strokeWidth ?? DEFAULT_STROKE_WIDTH,
          fillColor: styleOptions?.fillColor ?? DEFAULT_FILL_COLOR,
        }
      };

      feature.setId(id.toString());
      feature.setProperties({
        polygonId: id,
        ...polygonData.style
      });

      this.polygonSubject.next(polygonData);

      this.deactivatePolygonDrawing();

      // Ensure the flag is reset
      setTimeout(() => {
        this.isDrawingModeActive = false;
      }, 100);
    });

  }

  private deactivatePolygonDrawing(): void {
    document.body.classList.remove('mapDrawingMode');

    if (this.drawInteraction) {
      this.mapInstance?.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }
  }

  /**
* Adds a Polyline (LineString) to the map.
* @param coordinates - An array of [longitude, latitude] pairs defining the line.
* @param styleOptions - Optional styling for the line (stroke color and width).
* @param id - Optional ID for the line feature.
*/

  public addPolyline(coordinates: [number, number][], styleOptions?: StyleOptions, id?: string | number): void {
    const lineCoords = coordinates.map(coord => olProj.fromLonLat(coord));

    const lineString = new LineString(lineCoords);

    const defaultStyleOptions: Required<StyleOptions> = {
      strokeColor: '#0000ff',
      strokeWidth: 3,
      fillColor: 'transparent',
    };

    const mergedStyleOptions = { ...defaultStyleOptions, ...styleOptions };

    const lineStyle = new Style({
      stroke: new Stroke({
        color: mergedStyleOptions.strokeColor,
        width: mergedStyleOptions.strokeWidth,
      }),
      fill: new Fill({
        color: mergedStyleOptions.fillColor,
      }),
    });

    const lineFeature = new Feature({
      geometry: lineString,
    });

    if (id !== undefined && id !== null) {
      lineFeature.setId(id);
    }

    lineFeature.setStyle(lineStyle);
    this.addFeatureToVectorSource(lineFeature);
  }

  /**
   * @param id - A unique identifier for the Overlay.
   * @param coordinate - [longitude, latitude] pairs defining the coordinate.
   * @param text - The text content to display in the Overlay. Can be a string or an HTMLDivElement.
   * @param cssClass - Optional CSS class to add to the Overlay element for custom styling.
   */
  public addOrUpdateOverlay(id: string, coordinate: [number, number], text: string | HTMLDivElement, cssClass?: string): void {
    const coordinates = olProj.fromLonLat(coordinate);

    if (this.tooltips.has(id)) {

      const existingTooltipOverlay = this.tooltips.get(id);

      if (existingTooltipOverlay) {

        const tooltipElement = existingTooltipOverlay.getElement();
        if (typeof text === 'string') {
          tooltipElement.innerHTML = text;
        } else if (text instanceof HTMLElement) {
          tooltipElement.innerHTML = '';
          tooltipElement.appendChild(text);
        }
        existingTooltipOverlay.setPosition(coordinates);

        let newClassName = 'ol-tooltip';

        if (cssClass) {
          newClassName += ` ${cssClass}`;
        }

        tooltipElement.className = newClassName;

      }

    }

    else {
      const tooltipElement = document.createElement('div');

      let initialClassName = 'ol-tooltip';
      if (cssClass) {
        initialClassName += ` ${cssClass}`;
      }

      tooltipElement.className = initialClassName;
      if (typeof text === 'string') {
        tooltipElement.innerHTML = text;
      } else if (text instanceof HTMLElement) {
        tooltipElement.innerHTML = '';
        tooltipElement.appendChild(text);
      }

      const tooltipOverlay = new Overlay({
        element: tooltipElement,
        position: coordinates,
        offset: [0, 0]
      });

      this.mapInstance.addOverlay(tooltipOverlay);
      this.tooltips.set(id, tooltipOverlay);
    }

  }

  /**
   * Removes a Overlay from the map by its ID.
   * @param id - The ID of the Overlay to remove.
   */
  public removeOverlay(id: string): void {
    if (this.tooltips.has(id)) {
      const tooltipOverlay = this.tooltips.get(id);
      if (tooltipOverlay) {
        this.mapInstance.removeOverlay(tooltipOverlay);
        this.tooltips.delete(id);
        console.log(`Tooltip "${id}" removed.`);
      }
    } else {
      console.warn(`Tooltip with ID "${id}" not found for removal.`);
    }
  }

  /**
   * 
   * @param text 
   * @param cssClass - Optional CSS class to add to the Overlay element for custom styling.
   * @returns 
   */

  public generateMarkerHtml(text: string, cssClass?: 'start' | 'destination'): HTMLDivElement {

    const mainDiv = document.createElement('div');
    mainDiv.className = `ol-marker`;
    if (cssClass) {
      mainDiv.className += ` ${cssClass}`;
    }

    this.markerElement = mainDiv;

    const headDiv = document.createElement('div');
    headDiv.className = 'head';

    const textSpan = document.createElement('span');
    textSpan.className = 'marker-text';
    textSpan.textContent = text ?? '';

    const headBackgroundDiv = document.createElement('div');
    headBackgroundDiv.className = 'head-background';

    const headCenterDiv = document.createElement('div');
    headCenterDiv.className = 'head-center';

    headBackgroundDiv.append(headCenterDiv);
    headDiv.appendChild(textSpan);
    headDiv.appendChild(headBackgroundDiv);
    mainDiv.appendChild(headDiv);

    const baseDiv = document.createElement('div');
    baseDiv.className = 'base';
    mainDiv.appendChild(baseDiv);

    return mainDiv;

  }

}

